import { useEffect, useRef } from 'react';
import p5 from 'p5';
import { renderPixelArtCanvas } from '../../lib/pixel-art/render-pixel-art';

p5.disableFriendlyErrors = true;

const PIXEL_ART_DEBUG = process.env.NODE_ENV !== 'production';

const SKETCH_PIXEL_ART_OPTIONS = {
    enabled: true,
    width: 1750,
    dither: 'Ordered', // Dithering method ('Floyd-Steinberg', 'Ordered','2x2 Bayer', '4x4 Bayer',`Clustered 4x4` or `atkinson`)
    strength: 10, // Dithering strength (0-100)
    //palette: 'rgbg-36',        // Optional: Lospec palette slug (depends on Lospec API availability)
    //Recommended: Define a custom palette //  // You can also set `palette: null,` to use the colors from your original image //
    palette: null,
    resolution: 'original', // Use 'original' for full resolution, or 'pixel' for pixelated size
};

const logPixelArt = (...args) => {
    if (PIXEL_ART_DEBUG) {
        console.log('[pixel-art]', ...args);
    }
};

const Sketch = ({ artStyle, chartData }) => {
    const ref = useRef();

    useEffect(() => {
        if (!artStyle || !chartData?.values?.length) {
            logPixelArt('sketch skipped', {
                hasArtStyle: Boolean(artStyle),
                valueCount: chartData?.values?.length ?? 0,
            });
            return;
        }

        let isActive = true;
        let renderFrameId = null;
        let fallbackFrameId = null;
        let drawWasCalled = false;
        let sketchPalette = null;
        const container = ref.current;

        logPixelArt('sketch effect started', {
            valueCount: chartData.values.length,
            options: SKETCH_PIXEL_ART_OPTIONS,
        });

        const renderFinalCanvas = async (reason) => {
            logPixelArt('renderFinalCanvas started', { reason, isActive });

            const sourceCanvases = Array.from(
                container?.querySelectorAll(
                    'canvas:not([data-pixel-art-output="true"])'
                ) ?? []
            );
            const sourceCanvas = sourceCanvases[sourceCanvases.length - 1];

            if (!isActive || !sourceCanvas) {
                logPixelArt('renderFinalCanvas bailed', {
                    isActive,
                    hasContainer: Boolean(container),
                    hasSourceCanvas: Boolean(sourceCanvas),
                    sourceCanvasCount: sourceCanvases.length,
                });
                return;
            }

            logPixelArt('source canvas found', {
                width: sourceCanvas.width,
                height: sourceCanvas.height,
                styleWidth: sourceCanvas.style.width,
                styleHeight: sourceCanvas.style.height,
                sourceCanvasCount: sourceCanvases.length,
            });

            try {
                const pixelCanvas = await renderPixelArtCanvas(
                    sourceCanvas,
                    sketchPalette
                        ? {
                              ...SKETCH_PIXEL_ART_OPTIONS,
                              palette: sketchPalette,
                          }
                        : SKETCH_PIXEL_ART_OPTIONS
                );

                if (!isActive || !pixelCanvas || !container) {
                    logPixelArt('pixel canvas result ignored', {
                        isActive,
                        hasPixelCanvas: Boolean(pixelCanvas),
                        hasContainer: Boolean(container),
                    });
                    return;
                }

                container
                    .querySelector('[data-pixel-art-output="true"]')
                    ?.remove();
                sourceCanvases.forEach((canvas) => {
                    canvas.style.display = 'none';
                });
                container.appendChild(pixelCanvas);

                logPixelArt('pixel canvas appended', {
                    width: pixelCanvas.width,
                    height: pixelCanvas.height,
                    childCount: container.childElementCount,
                });
            } catch (error) {
                console.warn('Unable to render pixel art canvas:', error);
            }
        };

        const queuePixelRender = (reason) => {
            logPixelArt('queuePixelRender', { reason });
            if (renderFrameId) window.cancelAnimationFrame(renderFrameId);
            renderFrameId = window.requestAnimationFrame(() => {
                renderFinalCanvas(reason);
            });
        };

        const instance = new p5((p) => {
            artStyle(p, chartData);

            const originalSetup = p.setup;
            const originalDraw = p.draw;

            logPixelArt('p5 artStyle registered', {
                hasSetup: typeof originalSetup === 'function',
                hasDraw: typeof originalDraw === 'function',
            });

            p.setup = (...args) => {
                logPixelArt('p5 setup started');

                // Constrain canvas to frame container instead of full window
                const containerW = container.offsetWidth;
                const containerH = container.offsetHeight;
                if (containerW > 0 && containerH > 0) {
                    const origCreateCanvas = p.createCanvas.bind(p);
                    p.createCanvas = (_w, _h, ...rest) =>
                        origCreateCanvas(containerW, containerH, ...rest);
                }

                if (typeof originalSetup === 'function') {
                    originalSetup(...args);
                }

                if (p._generatedPalette?.length) {
                    sketchPalette = p._generatedPalette;
                }

                logPixelArt('p5 setup finished', {
                    canvasCount:
                        container?.querySelectorAll('canvas').length ?? 0,
                    width: p.width,
                    height: p.height,
                });

                fallbackFrameId = window.requestAnimationFrame(() => {
                    if (
                        isActive &&
                        !drawWasCalled &&
                        typeof originalDraw === 'function'
                    ) {
                        logPixelArt(
                            'p5 draw was not called; running fallback draw'
                        );
                        drawWasCalled = true;
                        originalDraw();
                        queuePixelRender('fallback-after-setup');
                    } else {
                        logPixelArt('fallback draw skipped', {
                            isActive,
                            drawWasCalled,
                            hasDraw: typeof originalDraw === 'function',
                        });
                    }
                });
            };

            p.draw = (...args) => {
                drawWasCalled = true;
                logPixelArt('p5 draw started');

                if (typeof originalDraw === 'function') {
                    originalDraw(...args);
                }

                logPixelArt('p5 draw finished');
                queuePixelRender('p5-draw');
            };
        }, container);

        return () => {
            logPixelArt('sketch cleanup');
            isActive = false;
            if (renderFrameId) window.cancelAnimationFrame(renderFrameId);
            if (fallbackFrameId) window.cancelAnimationFrame(fallbackFrameId);
            container
                ?.querySelector('[data-pixel-art-output="true"]')
                ?.remove();
            instance.remove();
        };
    }, [artStyle, chartData]);

    return <div ref={ref} className="sketch-container" />;
};

export default Sketch;
